import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import IconFont from '$/components/IconFont';
import { handleImageUrl } from '$/utils/image';
import { getVideoCoverUrl } from '$/utils/upload';
import { getInlineFileIconConfig } from '$/components/InlineBox/constants';
import type { IInlineBoxData } from '$/components/InlineBox/types';
import styles from './index.module.scss';

interface SuggestionOption {
  id: string;
  name: string;
  description?: string;
  image?: {
    url: string;
    coverUrl?: string;
    icon?: string;
    mediaType?: IInlineBoxData['mediaType'];
  };
  skill?: {
    url: string;
    coverUrl?: string;
    icon?: string;
    mediaType?: IInlineBoxData['mediaType'];
  };
}

interface Props {
  top: number;
  left: number;
  placement?: 'top' | 'bottom';
  options: SuggestionOption[];
  activeIndex: number;
  emptyText?: string;
  /** 数据为空时自定义渲染 */
  renderEmpty?: () => React.ReactNode;
  /** 列表首项自定义渲染 */
  renderBeforeItem?: () => React.ReactNode;
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelect: (option: any) => void;
}

/**
 * 按触发目标定位资源候选列表，角色与图片共用缩略图展示。
 */
export default function SuggestionPopup({
  top,
  left,
  placement = 'bottom',
  options,
  activeIndex,
  emptyText,
  renderEmpty,
  renderBeforeItem,
  onKeyDown,
  onSelect,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

  // 选项数量变化时清理失效节点，避免 activeIndex 边界变化后命中旧引用。
  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, options.length);
  }, [options.length]);

  // 键盘切换激活项时，仅滚动弹层自身，保证选中项进入可见区域。
  useEffect(() => {
    const container = containerRef.current;
    const activeItem = itemRefs.current[activeIndex];
    if (!container || !activeItem) return;

    const itemTop = activeItem.offsetTop;
    const itemBottom = itemTop + activeItem.offsetHeight;
    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;

    if (itemTop < viewTop) {
      container.scrollTop = itemTop;
      return;
    }

    if (itemBottom > viewBottom) {
      container.scrollTop = itemBottom - container.clientHeight;
    }
  }, [activeIndex, options.length]);

  /** 限制滚轮事件只在弹层内消费，避免滚到列表边界后带动主页面滚动。 */
  const handleWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) return;

    const {
      scrollTop,
      scrollHeight,
      clientHeight,
    } = container;
    const canScroll = scrollHeight > clientHeight;
    const isScrollingUp = event.deltaY < 0;
    const isScrollingDown = event.deltaY > 0;
    const atTop = scrollTop <= 0;
    const atBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;

    if (!canScroll || (isScrollingUp && atTop) || (isScrollingDown && atBottom)) {
      event.preventDefault();
    }

    event.stopPropagation();
  }, []);

  const emptyContent = (() => {
    if (renderEmpty) return renderEmpty();
    if (emptyText) return <div className={styles.empty}>{emptyText}</div>;
    return null;
  })();

  /** 鼠标按下时立即选择，避免编辑器 blur 先关闭弹层导致首次点击丢失。 */
  const handleItemMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>, option: SuggestionOption) => {
    event.preventDefault();
    onSelect(option);
  }, [onSelect]);

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, 'RoboneoSuggestionPopup')}
      style={{
        top,
        left,
        transform: placement === 'top' ? 'translateY(-100%)' : undefined,
      }}
      onKeyDown={onKeyDown}
      onWheel={handleWheel}
      role="listbox"
      tabIndex={-1}
    >
      {options.length === 0 ? emptyContent : (
        <div className={styles.list}>
          {renderBeforeItem?.()}
          { options.map((option, index) => (
            <div
              key={option.id}
              ref={(node) => {
                itemRefs.current[index] = node;
              }}
              role="option"
              tabIndex={0}
              aria-selected={index === activeIndex}
              className={classNames(styles.item, { [styles.active]: index === activeIndex })}
              onMouseDown={(event) => handleItemMouseDown(event, option)}
            >
              {(() => {
                const imgData = option.image ?? option.skill;
                if (!imgData) return null;
                const {
                  mediaType, url, coverUrl, icon,
                } = imgData;
                if (mediaType === 'video') {
                  return (
                    <div className={styles['thumbnail-wrapper']}>
                      <img
                        className={styles.thumbnail}
                        src={handleImageUrl(getVideoCoverUrl(url), { showWatermark: false, size: 152 })}
                        alt={option.name}
                        crossOrigin="anonymous"
                      />
                      <IconFont name="playFill" fontSize={16} className={styles['play-icon']} />
                    </div>
                  );
                }
                if (mediaType === 'file' || mediaType === 'skill') {
                  const {
                    icon: fileIcon,
                    background,
                    iconColor,
                  } = icon
                    ? { icon, background: 'var(--workspace-attachment-backgroundMarkdown, #94A3B8)', iconColor: 'var(--workspace-attachment-contentIconFill, #F8F9FA)' }
                    : getInlineFileIconConfig(url);
                  return (
                    <div className={styles['icon-box']} style={{ background }}>
                      <IconFont name={fileIcon} fontSize={16} color={iconColor} />
                    </div>
                  );
                }
                if (mediaType === 'input_ip' && !url && !coverUrl) {
                  return <IconFont name="portraitPhotoBold" fontSize={24} className={styles.thumbnail} />;
                }
                // 图片与有头像的角色共用缩略图。
                return (
                  <img
                    className={styles.thumbnail}
                    src={handleImageUrl(coverUrl || url, { showWatermark: false, size: 152 })}
                    alt={option.name}
                    crossOrigin="anonymous"
                  />
                );
              })()}
              <div className={styles.info}>
                <span className={styles.name}>{option.name}</span>
                {option.description ? <span className={styles.description}>{option.description}</span> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
